import type {
  NavigationLinkKey,
  NavigationLinkItem,
  NavigationModelLinkKey,
} from "./navigation-link.type";
import type { FeatureFlagId } from "@/config/environment";

import { useTranslation } from "react-i18next";

import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import { useAuthorization } from "@/modules/authorization";
import ModelIcon from "@/modules/model-icon";
import { useModelLinkInline } from "@/modules/model-link";
import { useStaticRouteLinkUtils } from "@/modules/static-route";

import {
  NAVIGATION_LINKS,
  NAVIGATION_MODEL_LINKS,
} from "./navigation-link.constant";

export const useNavigationLink = (
  key: NavigationLinkKey
): NavigationLinkItem | undefined => {
  const { t } = useTranslation();

  const { url, icon, external, featureFlag } = useNavigationLinkInfos(key);
  const isFeatureFlagActive = useFeatureFlag(featureFlag as FeatureFlagId);
  const showLink = featureFlag ? isFeatureFlagActive : true;

  if (!showLink) return undefined;

  const text = t(`navigation:${key}`);

  return {
    key,
    text,
    icon,
    url,
    external,
  };
};

const useNavigationLinkInfos = (key: NavigationLinkKey) => {
  const { getRouteLink } = useStaticRouteLinkUtils();

  const link = NAVIGATION_LINKS[key];
  const { icon, featureFlag = "" } = link;

  if ("external" in link) {
    return {
      url: link.url,
      external: true,
      icon,
      featureFlag,
    };
  }

  const { routeKey, search } = link;

  return {
    url: getRouteLink(routeKey, { search }),
    external: false,
    icon,
    featureFlag,
  };
};

export const useNavigationModelLink = (
  key: NavigationModelLinkKey
): NavigationLinkItem | undefined => {
  const { t } = useTranslation();
  const { canCreateModel, canViewModel, canUpdateModel } = useAuthorization();

  const { modelKey, authorization } = NAVIGATION_MODEL_LINKS[key];
  const url = useModelLinkInline(modelKey);

  if (!url) return undefined;
  if (authorization === "view" && !canViewModel(modelKey)) return undefined;
  if (authorization === "create" && !canCreateModel(modelKey)) return undefined;
  if (authorization === "update" && !canUpdateModel(modelKey)) return undefined;

  const text = t(`navigation:${key}`);
  const icon = <ModelIcon model={modelKey} />;
  const external = false;

  return {
    key,
    text,
    icon,
    url,
    external,
  };
};
